import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faEnvelopeOpenText,
  faFileCertificate,
  faFlaskVial,
  faMobile,
  faNoteMedical,
  faPrescription,
  faXRay,
} from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'

export type ServiceIconKind =
  | 'certificate'
  | 'prescription'
  | 'telehealth'
  | 'bloodTest'
  | 'radiology'
  | 'specialistReferral'
  | 'generalLetter'

type ServiceIconTheme = {
  icon: IconDefinition
  gradient: string
  flatBackground: string
  flatForeground: string
}

const serviceIconThemes: Record<ServiceIconKind, ServiceIconTheme> = {
  certificate: {
    icon: faFileCertificate,
    gradient: 'from-[#F8B45B] to-[#EF9938]',
    flatBackground: 'bg-[#FCECD2]',
    flatForeground: 'text-[#B15C10]',
  },
  prescription: {
    icon: faPrescription,
    gradient: 'from-[#9ADBC7] to-[#55BFA7]',
    flatBackground: 'bg-[#DFF2EC]',
    flatForeground: 'text-[#17614F]',
  },
  telehealth: {
    icon: faMobile,
    gradient: 'from-[#6A9FF6] to-[#2563EB]',
    flatBackground: 'bg-[#DEEAFB]',
    flatForeground: 'text-[#1E3A6E]',
  },
  bloodTest: {
    icon: faFlaskVial,
    gradient: 'from-[#A58AE8] to-[#7C5CD6]',
    flatBackground: 'bg-[#EFE7FB]',
    flatForeground: 'text-[#5B3FA8]',
  },
  radiology: {
    icon: faXRay,
    gradient: 'from-[#8792A7] to-[#5A6478]',
    flatBackground: 'bg-[#E9EBF0]',
    flatForeground: 'text-[#454F63]',
  },
  specialistReferral: {
    icon: faNoteMedical,
    gradient: 'from-[#8792A7] to-[#5A6478]',
    flatBackground: 'bg-[#E9EBF0]',
    flatForeground: 'text-[#454F63]',
  },
  generalLetter: {
    icon: faEnvelopeOpenText,
    gradient: 'from-[#8DB6D5] to-[#5A85AA]',
    flatBackground: 'bg-[#E8F0F8]',
    flatForeground: 'text-[#315E83]',
  },
}

type ServiceIconProps = {
  kind: ServiceIconKind
  variant?: 'gradient' | 'flat'
  className?: string
  iconClassName?: string
}

export function ServiceIcon({
  kind,
  variant = 'gradient',
  className,
  iconClassName,
}: ServiceIconProps) {
  const theme = serviceIconThemes[kind]

  return (
    <span
      aria-hidden="true"
      className={clsx(
        'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
        variant === 'gradient'
          ? ['bg-gradient-to-br text-white', theme.gradient]
          : [theme.flatBackground, theme.flatForeground],
        className,
      )}
    >
      <FontAwesomeIcon
        icon={theme.icon}
        className={clsx('h-4 w-4', iconClassName)}
      />
    </span>
  )
}
